<template>
  <base-page-template>
    <v-container fluid>
      <v-form v-model="valid" ref="form">
        <v-card outlined class="elevation-0" v-if="editable || tradeContract.isPublishMode">
          <v-card-title>確定と送信</v-card-title>
          <v-card-subtitle>
            <p>{{ subtitle }}</p>
            <error-panel :errors="errorsView" small></error-panel>
          </v-card-subtitle>

          <!-- 契約募集フローのみ -->
          <v-card-text :style="{paddingBottom: 0}" v-if="tradeContract.isPublishMode">
            <v-container class="py-0">
              <v-row dense class="form-row">
                <label class="v-label theme--light">申込期限</label>
                <v-col style="width: fit-content; flex-grow: 1; display: flex; align-items: center;" class="mr-10">
                  <el-date-picker
                      v-model="expireDate"
                      type="date"
                      :class="{'has-error': !expireDateIsValid, 'mr-3': true}"
                      :picker-options="{
                        disabledDate: test => {
                            const testEpocMillis = test.getTime();
                            const todayEpocMillis = today.valueOf();
                            return testEpocMillis < todayEpocMillis;
                          }
                        }"
                      :clearable="false">
                  </el-date-picker>
                  <error-panel
                      v-if="!expireDateIsValid"
                      :errors="[{
                        level: 'error',
                        message: '申込期限は翌日以降を設定して下さい。'
                      }]"
                      small>
                  </error-panel>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <!-- 契約募集フロー以外 -->
          <v-card-text :style="{paddingBottom: 0}" v-else>
            <v-container class="py-0">
              <v-row dense class="form-row">
                <label class="v-label theme--light">回答期限</label>
                <v-col style="width: fit-content; flex-grow: 1; display: flex; align-items: center;" class="mr-10">
                  <template v-if="tradeContract.status === 'requesting' || tradeContract.status === 'applying'">
                    <el-tooltip
                        :content="formatDate(expireDate)"
                        placement="right"
                        :open-delay="500">
                      <v-chip
                          class="el-date-picker-like"
                          color="grey darken-3"
                          label outlined>
                        <v-avatar left><v-icon small>el-icon-date</v-icon></v-avatar>
                        {{ tradeContract.expireDate.diffNow('days').toFormat('残り d 日') }}
                      </v-chip>
                    </el-tooltip>
                  </template>
                  <template v-else>
                    <el-date-picker
                        v-model="expireDate"
                        type="date"
                        :class="{'has-error': !expireDateIsValid, 'mr-3': true}"
                        :picker-options="{
                          disabledDate: test => {
                              const testEpocMillis = test.getTime();
                              const todayEpocMillis = today.valueOf();
                              return testEpocMillis < todayEpocMillis;
                            }
                          }"
                        :clearable="false">
                    </el-date-picker>
                    <error-panel
                        v-if="!expireDateIsValid"
                        :errors="[{
                          level: 'error',
                          message: '回答期限は翌日以降を設定して下さい。'
                        }]"
                        small>
                    </error-panel>
                  </template>
                </v-col>
              </v-row>
              <v-row dense align-content="center" class="form-row">
                <label class="v-label theme--light">契約期間</label>
                <v-col>
                  <el-date-picker
                      v-model="startDatetime"
                      type="datetime"
                      placeholder="開始日時"
                      :picker-options="{
                        disabledDate: test => {
                          const testEpocMillis = test.getTime();
                          return tradeContract.endDatetime &&
                            testEpocMillis > tradeContract.endDatetime.startOf('day').toMillis();
                        }
                      }">
                  </el-date-picker>
                </v-col>
                <v-col style="line-height: 40px;" class="mx-1">
                  <span style="vertical-align: middle;">〜</span>
                </v-col>
                <v-col class="mr-2">
                  <el-date-picker
                      v-model="endDatetime"
                      type="datetime"
                      placeholder="終了日時"
                      :picker-options="{
                        disabledDate: test => {
                          const testEpocMillis = test.getTime();
                          return tradeContract.startDatetime &&
                            testEpocMillis < tradeContract.startDatetime.startOf('day').toMillis();
                        }
                      }">
                  </el-date-picker>
                </v-col>
                <v-col style="width: 100px;" class="mr-10">
                  <v-btn
                      @click="tradeContract.startDatetime = new Date(); tradeContract.endDatetime = null;"
                      color="indigo lighten-1 white--text"
                      style="height: 38px; top: 1px; position: relative;"
                      elevation="0"
                      depressed
                      small>すぐに開始</v-btn>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="3">
                  <v-checkbox
                      v-if="!tradeContract.isPublishMode && !tradeContract.isSubscribeMode"
                      v-model="tradeContract.unchangeable"
                      label="修正不可"
                      :readonly="tradeContract.amiRecipient"
                      hide-details>
                  </v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <!-- 遷移ボタン -->
          <v-card-actions>
            <v-row dense align="center"
                   class="mt-3"
                   :justify="tradeContract.isPublishMode ? 'center' : 'space-around'">
              <div v-if="!tradeContract.isPublishMode"
                   class="mr-1"
                   style="flex-grow: 1;">
                <v-textarea
                    class="message-textarea"
                    v-model="tradeContract.message"
                    label="メッセージ (複数行入力可)"
                    :rules="[v => !!v || 'メッセージを入力してください']"
                    rows="1"
                    auto-grow
                    outlined
                    autofocus>
                </v-textarea>
              </div>
              <div style="padding-bottom: 30px;">
                <template v-for="(button, index) in transitButtons">
                  <el-tooltip :key="`${index}-${button.status}`" placement="bottom" :content="button.description">
                    <v-btn
                        v-if="button.requestType === 'save' || (!button.status || tradeContract.status === button.status) && tradeContract.transitable(button.requestType)"
                        :color="button.color"
                        x-large
                        :class="button['class'] || 'mx-1 my-3'"
                        @click="doTransit(button)"
                        :disabled="!isTransitButtonEnabled(button)">
                      {{ button.text }}
                    </v-btn>
                  </el-tooltip>
                </template>
              </div>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-form>

      <!-- メッセージ履歴 (契約募集フロー以外) -->
      <message-history
          v-if="!tradeContract.isPublishMode"
          :trade-contract="tradeContract">
      </message-history>
    </v-container>

    <!-- 承認確認ダイアログ -->
    <simple-dialog
        :set="button = (acceptConfirmDialog.button || {})"
        :value="acceptConfirmDialog.visible"
        @close="acceptConfirmDialog.visible = false"
        title-text="契約内容の最終確認"
        apply-text=""
        :header-text="`契約内容の最終確認です。${button.text}してよろしければ下部の${button.text}ボタンを押下して下さい。`"
        no-padding>
      <v-container>
        <trade-contract-view-panel
            :trade-contracts="tradeContracts"
            :activate="acceptConfirmDialog.visible"
            hide-other-data-profile
            hide-other-event-profile
        >
        </trade-contract-view-panel>
        <v-textarea
            :value="acceptConfirmDialog.agreement"
            :loading="acceptConfirmDialog.agreement === null"
            rows="10"
            class="mt-5"
            outlined
            readonly
            hide-details>
        </v-textarea>
      </v-container>
      <v-row justify="center" class="my-10">
        <v-btn
            :color="button.color"
            x-large
            :class="button['class'] || 'mx-5 my-3'"
            @click="button.confirmed = true; doTransit(button);">
          {{ button.text }}
        </v-btn>
      </v-row>
    </simple-dialog>

    <app-header :errors="errorsView">
      <error-panel :errors="errorsView" small></error-panel>
    </app-header>
  </base-page-template>
</template>

<script>
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import {mapGetters} from "vuex";
import Formatter from "@/utils/Formatter";
import {CONTRACT_MESSAGE_REQUEST_TYPES} from "@/constants";
import Swal from "sweetalert2";
import TradeContractView, {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import {safeMeta} from "@/router/Meta";
import TradeContractViewPanel from "@/components/pages/contract/TradeContract/TradeContractViewPanel.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import axios from "axios";
import {DateTime} from "luxon";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";
import AppHeader from "@/components/atoms/AppHeader.vue";
import MessageHistory from "@/components/pages/contract/TradeContract/MessageHistory.vue";

export default {
  name: "Complete",
  components: {MessageHistory, AppHeader, ErrorPanel, SimpleDialog, TradeContractViewPanel, BasePageTemplate},
  mixins: [Formatter],
  props: {
    tradeContracts: {
      type: Array,
      required: true
    },
    completable: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => ([])
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      valid: false,
      expireDateMenu: false,
      acceptConfirmDialog: {
        visible: false,
        agreement: null,
        button: null,
      },
    };
  },
  mounted() {
    this.$watch(
        () => this.tradeContract.id,
        tradeContractId => {
          if(tradeContractId) {
            this.load();
          }
        },
        { immediate: true }
    );
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    tradeContract() {
      return this.tradeContracts[0] ?? emptyTradeContractView();
    },
    transitButtons() {
      return [
      // 編集中ステータスの場合
      {
        status: 'preparing',
        requestType: 'publish',
        color: 'primary',
        text: '募集',
        description: '取引契約の募集を送信し、募集中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      }, {
        status: 'preparing',
        requestType: 'subscribe',
        color: 'primary',
        text: '申込',
        description: '取引契約の申込を送信し、申込中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      }, {
        status: 'preparing',
        requestType: 'request',
        color: 'primary',
        text: '申請',
        description: '取引契約の申請を送信し、申請中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      },
      // 募集中ステータスの場合
      {
        status: 'public',
        requestType: 'suspend',
        color: 'blue-grey darken-2 white--text',
        text: '停止',
        description: '取引契約募集の停止を送信し、募集停止ステータスに変更します。その後、取引契約管理画面に戻ります。',
      },
      // 募集停止ステータスの場合
      {
        status: 'private',
        requestType: 'resume',
        color: 'primary',
        text: '再開',
        description: '取引契約募集の再開を送信し、募集中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      },
      // 申請中ステータスの場合
      {
        status: 'requesting',
        requestType: 'revise',
        color: 'blue-grey darken-2 white--text',
        text: '差戻',
        description: '取引契約の差戻しを送信し、申請中ステータスに変更します。その後、取引契約管理画面に戻ります。',
      }, {
        status: 'requesting',
        requestType: 'reject',
        color: 'blue-grey darken-4 white--text',
        text: '拒否',
        description: '取引契約の拒否を送信し、不成立ステータスに変更します。その後、取引契約管理画面に戻ります。',
      }, {
        status: 'requesting',
        requestType: 'accept',
        color: 'primary',
        text: '承認',
        description: '取引契約の承認を送信し、取引中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      },
      // 申込中ステータスの場合
      {
        status: 'applying',
        requestType: 'revise',
        color: 'blue-grey darken-2 white--text',
        text: '差戻',
        description: '取引契約の差戻しを送信し、申請中ステータスに変更します。その後、取引契約管理画面に戻ります。',
      }, {
        status: 'applying',
        requestType: 'reject',
        color: 'blue-grey darken-4 white--text',
        text: '拒否',
        description: '取引契約の拒否を送信し、不成立ステータスに変更します。その後、取引契約管理画面に戻ります。',
      }, {
        status: 'applying',
        requestType: 'accept',
        color: 'primary',
        text: '承認',
        description: '取引契約の承認を送信し、取引中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      },
      // 修正中ステータスの場合
      {
        status: 'revising',
        requestType: 'cancel',
        color: 'blue-grey darken-4 white--text',
        text: '取消',
        description: '取引契約の取消を送信し、不成立ステータスに変更します。その後、取引契約管理画面に戻ります。',
      }, {
        status: 'revising',
        requestType: 'request',
        color: 'primary',
        text: '申請',
        description: '取引契約の申請を送信し、申請中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      }, {
        status: 'revising',
        requestType: 'subscribe',
        color: 'primary',
        text: '申請',
        description: '取引契約の申請を送信し、申請中ステータスに変更します。その後、取引契約管理画面に戻ります。',
        complete: true,
      }];
    },
    subtitle() {
      return safeMeta(this.$route).subtitle;
    },
    today() {
      return DateTime.now().startOf('day');
    },
    expireDate: {
      get() {
        return this.tradeContract.expireDate?.toJSDate();
      },
      set(date) {
        if(date) {
          const expireDate = DateTime.fromJSDate(date).set({
            hour: 23, minute: 59, second: 59
          });
          this.tradeContracts.forEach(tc => tc.expireDate = expireDate);
        }
      }
    },
    expireDateIsValid() {
      return this.tradeContract.expireDateIsValid;
    },
    startDatetime: {
      get() {
        return this.tradeContract.startDatetime?.toJSDate();
      },
      set(datetime) {
        if(this.tradeContract.endDatetime && datetime) {
          const endEpocMillis = this.tradeContract.endDatetime.toMillis();
          const startEpocMillis = datetime.getTime();
          if(startEpocMillis > endEpocMillis) {
            return;
          }
        }
        this.tradeContracts.forEach(tc => tc.startDatetime = datetime);
      }
    },
    endDatetime: {
      get() {
        return this.tradeContract.endDatetime?.toJSDate();
      },
      set(datetime) {
        if(this.tradeContract.startDatetime && datetime) {
          const endEpocMillis = datetime.getTime();
          const startEpocMillis = this.tradeContract.startDatetime.toMillis();
          if(startEpocMillis > endEpocMillis) {
            return;
          }
        }
        this.tradeContracts.forEach(tc => tc.endDatetime = datetime);
      }
    },
    errorsView() {
      const errors = this.errors.slice();
      const buttons = this.transitButtons
          .filter(button => this.tradeContract.status === button.status && this.tradeContract.transitable(button.requestType))
          .map(button => button.text)
          .join('ボタン・')
      if(!this.expireDateIsValid) {
        errors.push({
          level: 'error',
          message: (() => {
            if(this.tradeContract.status === 'requesting' || this.tradeContract.status === 'applying') {
              return this.tradeContract.isPublishMode ? `申込期限が過ぎています。` : `回答期限が過ぎています。`;
            } else {
              return this.tradeContract.isPublishMode ? `申込期限は翌日以降を設定して下さい。` : `回答期限は翌日以降を設定して下さい。`;
            }
          })()
        });
      }
      if(!this.tradeContract.message) {
        errors.push({
          level: 'error',
          message: `メッセージを入力することで${buttons}ボタンが押下可能になります。`
        });
      }
      if(errors.length === 0) {
        errors.push({
          level: 'good',
          message: `${buttons}ボタンを押下して取引先にメッセージとステータスを送信する事ができます。`
        })
      }
      return errors;
    }
  },
  methods: {
    async load() {
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    doTransit(button) {
      if(button.complete && !button.confirmed) {
        if(this.tradeContract.message) {
          this.acceptConfirmDialog.visible = true;
          this.acceptConfirmDialog.button = {...button};
          if (this.acceptConfirmDialog.agreement === null) {
            axios.get('/contract_agreement.txt').then(response => {
              this.acceptConfirmDialog.agreement = response.data;
            })
          }
        }
        return;
      }

      Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に${button.text}してよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      }).then(result => {
        if (result.value) {
          this.$emit('transit', button.requestType);
        }
      });
    },
    isTransitButtonEnabled(button) {
      // 中断ボタンは常に押下可
      if(button.requestType === 'save') {
        return true;
      }
      // メッセージが未入力の場合は全ボタン押下不可
      if(!this.tradeContract.message) {
        return false;
      }
      // complete フラグの立っているボタンは completable と同期する
      if(button.complete) {
        return this.completable;
      }
      // complete フラグの立っていないボタンは常に押下可
      return true;
    },
  },
}
</script>

<style lang="scss" scoped>
$el-override-color: rgba(0, 0, 0, 0.42);
$el-override-emphasis-color: rgba(0, 0, 0, 0.87);
$el-override-placeholder-color: rgb(96, 98, 102);

::v-deep .el-date-editor {
  .el-input__inner {
    border-color: $el-override-color;
    color: $el-override-emphasis-color;

    &::placeholder {
      color: $el-override-placeholder-color;
    }

    &:hover {
      border-color: $el-override-emphasis-color;
    }
  }

  // エラーの場合に赤くする
  &.has-error .el-input__inner {
    border-color: rgb(180,0,0);
    box-shadow: 0 0 5px rgb(220 0 0 / 50%);
  }

  &.el-date-editor--datetimerange {
    border-color: $el-override-color;

    .el-range__icon {
      color: $el-override-emphasis-color;
    }

    .el-range-input {
      color: $el-override-emphasis-color;

      &::placeholder {
        color: $el-override-placeholder-color;
      }
    }

    &:hover {
      border-color: $el-override-emphasis-color;
    }
  }

  .el-input__prefix {
    color: $el-override-placeholder-color;
  }
}

.row.form-row {
  .col {
    width: fit-content;
    flex-grow: initial;
  }

  label {
    margin-right: 1rem;
    white-space: nowrap;
    align-self: center;
  }
}

.v-application .v-chip.el-date-picker-like {
  width: 220px;
  height: 40px;
  border-color: rgba(0, 0, 0, 0.42) !important;

  &:hover {
    border-color: rgba(0, 0, 0, 0.87) !important;
  }
  &:hover:before {
    opacity: 0;
  }
}

.message-textarea ::v-deep textarea {
  margin-top: 16px !important;
  margin-bottom: 8px;
}

</style>
